import React from 'react';
import { Box, Button, ButtonBase, Checkbox, Chip, Grid, Skeleton, Tooltip, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { alpha } from '@mui/material/styles';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { EmptyState } from '../../../../components/empty-state';
import { FieldError } from '../../../../components/form-page-parts';
import { WEEK_DAYS } from '../../../../model/ser/ser-enums';
import { ISubmissionVehicleResponse } from '../../../../model/ser/submission/response/submission-vehicle-response';
import { SubmissionDraft, computeVehicleDeadline } from '../submission-send-rules';
import { StepSection } from './step-section';
import { VehicleDeadlineLine } from './vehicle-deadline-line';

// Dias de publicação do veículo (D S T Q Q S S), com destaque nos dias em que publica
export function WeekDayDots({ weekDays }: { weekDays?: number[] }) {
    const everyDay = !weekDays || weekDays.length === 0;
    return (
        <Box sx={{ display: 'flex', gap: 0.5 }} aria-label="Dias de publicação">
            {WEEK_DAYS.map((day) => {
                const active = everyDay || weekDays!.includes(day.id);
                return (
                    <Tooltip key={day.id} title={`${day.name}${active ? '' : ' (não publica)'}`}>
                        <Box
                            sx={{
                                width: 22, height: 22, borderRadius: '50%', fontSize: 11, fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: active ? 'primary.main' : 'action.hover',
                                color: active ? 'primary.contrastText' : 'text.disabled',
                            }}
                        >
                            {day.initial}
                        </Box>
                    </Tooltip>
                );
            })}
        </Box>
    );
}

function VehicleCard({ vehicle, selected, publishDate, onToggle }: { vehicle: ISubmissionVehicleResponse; selected: boolean; publishDate: string | null; onToggle: () => void }) {
    const deadline = computeVehicleDeadline(vehicle, publishDate);
    // Diário próprio: sempre marcado. Prazo encerrado para a data: não pode ser marcado (só desmarcado, se já estava)
    const locked = !!vehicle.isDefault;
    const blocked = !locked && deadline.expired && !selected;
    return (
        <ButtonBase
            role="checkbox"
            aria-checked={selected}
            aria-disabled={locked || blocked}
            disabled={locked || blocked}
            onClick={onToggle}
            sx={(theme) => ({
                opacity: blocked ? 0.6 : 1,
                width: '100%', height: '100%', p: 2, borderRadius: 2, textAlign: 'left',
                display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1.5,
                border: '2px solid', borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: blocked ? 'action.hover' : selected ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                transition: 'border-color 150ms ease-out, background-color 150ms ease-out',
                '&:hover': { borderColor: selected ? 'primary.main' : alpha(theme.palette.primary.main, 0.5) },
                '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
            })}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ display: 'flex', p: 0.75, borderRadius: 1.5, bgcolor: selected ? 'primary.main' : 'action.hover', color: selected ? 'primary.contrastText' : 'text.secondary' }}>
                    <NewspaperIcon fontSize="small" />
                </Box>
                <Typography fontWeight={700} sx={{ flex: 1, minWidth: 0, pt: 0.5, wordBreak: 'break-word' }}>{vehicle.name}</Typography>
                {locked
                    ? <Tooltip title="Sempre incluído nas remessas"><LockIcon color="primary" fontSize="small" sx={{ m: 0.5 }} /></Tooltip>
                    : <Checkbox checked={selected} disabled={blocked} tabIndex={-1} disableRipple sx={{ p: 0.25 }} inputProps={{ 'aria-hidden': true }} />}
            </Box>
            {locked && <Chip size="small" color="primary" variant="outlined" icon={<LockIcon />} label="Diário oficial próprio" sx={{ alignSelf: 'flex-start' }} />}
            <WeekDayDots weekDays={vehicle.weekDays} />
            <VehicleDeadlineLine deadline={deadline} publishDate={publishDate} />
        </ButtonBase>
    );
}

interface VehiclesStepProps {
    draft: SubmissionDraft;
    vehicles: ISubmissionVehicleResponse[];
    loading: boolean;
    error?: string;
    onToggle: (id: number) => void;
    onSelectAll: () => void;
}

export function VehiclesStep({ draft, vehicles, loading, error, onToggle, onSelectAll }: VehiclesStepProps) {
    // Considera só os que podem ser escolhidos (sem o diário próprio, fixo, e sem os de prazo encerrado)
    const selectable = vehicles.filter((v) => !v.isDefault && !computeVehicleDeadline(v, draft.publishDate).expired);
    const allSelected = selectable.length > 0 && selectable.every((v) => draft.vehicleIds.includes(v.id));

    return (
        <StepSection
            title="Veículos de publicação"
            description="Escolha onde a remessa será publicada. Veículos com o prazo de envio encerrado para a data informada não podem ser selecionados."
            action={selectable.length > 1 && (
                <Button size="small" startIcon={<DoneAllIcon />} onClick={onSelectAll}>
                    {allSelected ? 'Limpar seleção' : 'Selecionar todos'}
                </Button>
            )}
        >
            {loading && (
                <Grid container spacing={2}>
                    {[0, 1].map((i) => <Grid item xs={12} sm={6} key={i}><Skeleton variant="rounded" height={132} /></Grid>)}
                </Grid>
            )}
            {!loading && vehicles.length === 0 && (
                <EmptyState icon={<NewspaperIcon />} message="Não há veículos de publicação ativos para esta entidade. Fale com a equipe de publicação para liberar o envio." />
            )}
            {!loading && vehicles.length > 0 && (
                <Grid container spacing={2}>
                    {vehicles.map((vehicle) => (
                        <Grid item xs={12} sm={6} key={vehicle.id}>
                            <VehicleCard vehicle={vehicle} selected={draft.vehicleIds.includes(vehicle.id)} publishDate={draft.publishDate} onToggle={() => onToggle(vehicle.id)} />
                        </Grid>
                    ))}
                </Grid>
            )}
            <FieldError message={error} />
        </StepSection>
    );
}
